import { UseMelioFormResults } from '@melio/penny';
import { PaymentSettingsCalculations, Vendor } from '@melio/platform-api';
import { useEffect } from 'react';

import { getVendorEmail } from '../../single-payment/utils/scheduleFlowStepsUtils';
import { PaymentFlowFormFields, PaymentFlowOnChangeHandlers } from '../types';

export const useFormChangeHandlers = ({
  form,
  paymentSettings,
  onChangeCallback,
  vendors,
}: {
  form: UseMelioFormResults<PaymentFlowFormFields>;
  paymentSettings: PaymentSettingsCalculations | undefined;
  onChangeCallback: VoidFunction;
  vendors: Vendor[];
}): PaymentFlowOnChangeHandlers => {
  const { setValue } = form;

  const onVendorChange = (vendorId: string) => {
    setValue('vendorId', vendorId);
    setValue('deliveryMethodId', undefined);
    const selectedVendor = vendors.find((v) => v.id === vendorId);
    setValue('vendorEmail', getVendorEmail(selectedVendor));
    onChangeCallback();
  };

  const onDeliveryMethodChange = (deliveryMethodId: string) => {
    setValue('deliveryMethodId', deliveryMethodId);
    onChangeCallback();
  };

  const onAmountChange = (amount: string) => {
    setValue('amountToPay', amount);
    onChangeCallback();
  };

  const onFundingSourceChange = (fundingSourceId: string) => {
    setValue('fundingSourceId', fundingSourceId);
    onChangeCallback();
  };

  const onDeliveryDateChange = (date: Date) => {
    setValue('deliveryDate', date);
    onChangeCallback();
  };

  const onBillChange: PaymentFlowOnChangeHandlers['onBillChange'] = (bill) => {
    setValue('vendorId', bill.vendorId);
    setValue('amountToPay', bill.amount);
    setValue('deliveryMethodId', undefined);
    setValue('deliveryPreference', undefined);
    const selectedVendor = vendors.find((v) => v.id === bill.vendorId);
    setValue('vendorEmail', getVendorEmail(selectedVendor));
    onChangeCallback();
  };

  const onRecurrenceChange: PaymentFlowOnChangeHandlers['onRecurrenceChange'] = (data) => {
    const { recurrenceType } = data;

    setValue('recurrenceType', recurrenceType);
    onChangeCallback();
  };

  useEffect(() => {
    if (paymentSettings) {
      const { deliveryMethodId, fundingSourceId, deliveryPreferenceType, deliveryPreferenceOptions } = paymentSettings;
      setValue('deliveryMethodId', deliveryMethodId);
      setValue('fundingSourceId', fundingSourceId);
      setValue('deliveryPreference', deliveryPreferenceType);

      if (deliveryPreferenceOptions?.[0]) {
        const deliveryPreferenceOption = deliveryPreferenceOptions[0];
        setValue('deliveryDate', deliveryPreferenceOption.minDeliveryDate);
        setValue('scheduleDate', deliveryPreferenceOption.effectiveScheduleDate);
      }
    }
  }, [paymentSettings, setValue]);

  return {
    onVendorChange,
    onBillChange,
    onDeliveryMethodChange,
    onAmountChange,
    onFundingSourceChange,
    onDeliveryDateChange,
    onRecurrenceChange,
  };
};
